import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Checkbox,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import LoadingButton from '@mui/lab/LoadingButton';
import { Controller } from 'react-hook-form';
import { useGetMoodleConfigs } from '../../../../hooks/queries/moodle/useGetMoodleConfigs';
import { useCreateMoodleConfigForm } from '../../../../hooks/forms/moodle/useCreateMoodleConfigForm';

const CONFIG_TABLE_COLUMNS = [
  { id: 'baseUrl', label: 'Base URL' },
  { id: 'serviceName', label: 'Service Name' },
  { id: 'apiToken', label: 'API Token' },
  { id: 'isActive', label: 'Active' },
  { id: 'createdAt', label: 'Created At' },
];

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
};

const maskToken = (token) => {
  if (!token || token.length <= 8) return '••••••••';
  return token.slice(0, 4) + '••••••••' + token.slice(-4);
};

const MoodleConfig = () => {
  const [expanded, setExpanded] = useState(false);

  const { configs, loading } = useGetMoodleConfigs();
  const { form, onSubmit, isPending } = useCreateMoodleConfigForm(() =>
    setExpanded(false),
  );

  const {
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <Box padding={2}>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setExpanded((prev) => !prev)}
        >
          Add Configuration
        </Button>
      </Box>

      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
        sx={{ mb: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={500}>New Moodle Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <form onSubmit={onSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Base URL"
                  fullWidth
                  placeholder="https://moodle.example.com"
                  error={!!errors.baseUrl}
                  helperText={errors.baseUrl?.message}
                  {...register('baseUrl')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Service Name"
                  fullWidth
                  error={!!errors.serviceName}
                  helperText={errors.serviceName?.message}
                  {...register('serviceName')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="API Token"
                  fullWidth
                  error={!!errors.apiToken}
                  helperText={errors.apiToken?.message}
                  {...register('apiToken')}
                />
              </Grid>

              <Grid item xs={12} md={6} display="flex" alignItems="center">
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Active"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <LoadingButton
                  type="submit"
                  variant="contained"
                  loading={isPending}
                >
                  Save Configuration
                </LoadingButton>
              </Grid>
            </Grid>
          </form>
        </AccordionDetails>
      </Accordion>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 800 }}>
          <TableHead>
            <TableRow>
              {CONFIG_TABLE_COLUMNS.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{ fontWeight: 600, whiteSpace: 'nowrap', px: 2 }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={CONFIG_TABLE_COLUMNS.length}
                  align="center"
                >
                  Loading...
                </TableCell>
              </TableRow>
            ) : !Array.isArray(configs) || configs.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={CONFIG_TABLE_COLUMNS.length}
                  align="center"
                >
                  No Moodle configurations found.
                </TableCell>
              </TableRow>
            ) : (
              configs.map((row, index) => (
                <TableRow key={row.id ?? index} hover>
                  <TableCell sx={{ px: 2 }}>{row.baseUrl}</TableCell>
                  <TableCell sx={{ px: 2 }}>{row.serviceName}</TableCell>
                  <TableCell
                    sx={{ px: 2, fontFamily: 'monospace', letterSpacing: 1 }}
                  >
                    {maskToken(row.apiToken)}
                  </TableCell>
                  <TableCell sx={{ px: 2 }}>
                    <Chip
                      label={row.isActive ? 'Active' : 'Inactive'}
                      color={row.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', px: 2 }}>
                    {formatDate(row.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MoodleConfig;
